import { Component, inject, signal, type WritableSignal } from '@angular/core';
import { CardHeader } from '../../../../shared/components/card/card-header/card-header';
import { Card } from '../../../../shared/components/card/card/card';
import { Navbar } from '../../../../shared/components/navbar/navbar';
import { TablePermissions } from "../../../../shared/tables/table-permissions/table-permissions";
import { DialogModule } from 'primeng/dialog';
import { FormBuilder, FormControl, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { ToggleSwitchModule, type ToggleSwitchChangeEvent } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import type { Role, UpdateRolePayload } from '../../../../interfaces/application';
import { ButtonModule } from 'primeng/button';
import { RolesService } from '../../../../services/roles.service';
import { finalize } from 'rxjs';
import type { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-permissions',
  imports: [
    Navbar,
    Card,
    CardHeader,
    TablePermissions,
    DialogModule,
    ReactiveFormsModule,
    ToggleSwitchModule,
    ButtonModule
],
  templateUrl: './permissions.html',
  styleUrl: './permissions.css'
})
export class Permissions {

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _rolesService: RolesService = inject(RolesService);

  public loading: WritableSignal<boolean> = signal<boolean>(false);

  public permissionDialog: boolean = false;

  public permissionForm: FormGroup = this._fb.group({
    id: [null],
    name: [null],
    permissions: [[]],
  });

  private roleControls = new Map<string, FormControl>();

  get permissionName(): string {
    return this.permissionForm.get('name')?.value;
  }

  get permissions(): string[] {
    return this.permissionForm.get('permissions')?.value;
  }

  public ngOnInit(): void {
    this._syncRoleControls();
  }

  private _syncRoleControls(): void {
    this.roles.forEach(role => {
      const isActive = this.isActiveRole(role.label);
      const existingControl = this.roleControls.get(role.label);

      if (existingControl) {
        existingControl.setValue(isActive, { emitEvent: false });
        return;
      }

      this.roleControls.set(role.label, new FormControl(isActive));
    });
  }

  private _updatePermissions(permissions: string[]): void {
    this.permissionForm.patchValue({ permissions: [...new Set(permissions)] });
    this._syncRoleControls();
  }

  private _toggleLoading(): void {
    this.loading.set(!this.loading());
  }

  public closeDialog(): void {
    this.permissionDialog = false;
    this.permissionForm.reset();
    this.permissionForm.patchValue({ permissions: [] });
    this._syncRoleControls();
  }

  public openDialog(role: Role): void {
    this.permissionDialog = true;

    this.permissionForm.patchValue({
      ...role,
      permissions: [...new Set(role.permissions.map((permission) => permission.name))],
    });

    this._syncRoleControls();
  }

  public getRoleControl(label: string): FormControl {
    return this.roleControls.get(label)!;
  }

  public editRole(role: Role) {
    this.openDialog(role);
  }

  public submitDialog(): void {
    const id = this.permissionForm.get('id')?.value;
    const permissions = [...new Set(this.permissions || [])];

    if (!permissions.length) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Selecione ao menos uma permissão',
        detail: 'A API não aceita salvar um perfil sem permissões.',
        life: 3000
      });
      return;
    }

    const payload: UpdateRolePayload = { permissions };

    this._toggleLoading();

    this._rolesService.put(id, payload)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: () => {
          this._messageService.add({
            severity: 'success',
            summary: 'Permissão atualizada',
            life: 3000
          });
          this.closeDialog();
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao atualizar permissão',
          detail: error.error.message,
          life: 3000
        }),
      });
  }

  public isActiveRole(label: string): boolean {
    return this.roles.find((role) => role.label === label)!.permissions.every((permission) => this.permissions.includes(permission));
  }

  public onRoleChange(label: string, e: ToggleSwitchChangeEvent): void {
    const role = this.roles.find((role) => role.label === label)!;
    const currentPermissions = [...new Set(this.permissions || [])];

    if (e.checked) {
      this._updatePermissions([...currentPermissions, ...role.permissions]);
      return;
    }

    this._updatePermissions(currentPermissions.filter(permission => !role.permissions.includes(permission)));
  }

  public roles = [
    {
      label: 'Dashboard',
      permissions: ['view dashboard', 'get dashboard']
    },
    {
      label: 'Relatórios e Análises (Cohort)',
      permissions: ['view reports cohort', 'get reports cohort']
    },
    {
      label: 'Extrato',
      permissions: ['view statements', 'get statements']
    },
    {
      label: 'Área de Pagamentos (Visualizar)',
      permissions: ['view payments', 'get payments']
    },
    {
      label: 'Área de Pagamentos (Cadastrar)',
      permissions: ['create payments']
    },
    {
      label: 'Contrato',
      permissions: ['view contracts', 'get contracts']
    },
    {
      label: 'Comunicados',
      permissions: ['view announcements', 'get announcements']
    },
    {
      label: 'Usuários (Visualizar)',
      permissions: ['view users', 'get users']
    },
    {
      label: 'Usuários (Convidar)',
      permissions: ['invite users']
    },
    {
      label: 'Métricas Apostadores',
      permissions: ['get utm bettors']
    },
  ];
}
